import { USER_ROLE } from '@/shared/constants';
import { getInstructorMe } from '@/lib/api/generated/instructor-me/instructor-me';
import { getLesson } from '@/lib/api/generated/lesson/lesson';
import { getStudent } from '@/lib/api/generated/student/student';

const { instructorMeGetMyLessons } = getInstructorMe();
const { lessonGetMyExamLessons } = getLesson();
const { studentGetClasses } = getStudent();

export interface MyLessonRowDto {
  lessonId: number;
  lessonName: string;
  ended: boolean;
}

/** my-lessons 엔드포인트를 지원하는 역할 (원장·직원·로그아웃 상태는 해당 없음) */
export type MyLessonsRole = typeof USER_ROLE.INSTRUCTOR | typeof USER_ROLE.STUDENT | typeof USER_ROLE.PARENT;

/** 호출 전 역할을 좁히기 위한 타입 가드 */
export const isMyLessonsRole = (role: string): role is MyLessonsRole =>
  role === USER_ROLE.INSTRUCTOR || role === USER_ROLE.STUDENT || role === USER_ROLE.PARENT;

/** 강사 본인이 담당하는 수업 목록 */
const getInstructorMyLessons = async (): Promise<MyLessonRowDto[]> => {
  const res = await instructorMeGetMyLessons();
  if (!res.success) throw new Error(res.error?.message ?? '수업 목록을 불러오지 못했습니다.');
  return (res.data ?? []).map((item) => ({
    lessonId: item.id ?? 0,
    lessonName: item.lessonName ?? '',
    ended: item.ended ?? false,
  }));
};

/** 학생 Classroom 메뉴용 — 종료(COMPLETED)된 수업은 빼고 예정/진행 중인 수업만 (`GET /api/students/classes`) */
const getStudentOngoingLessons = async (): Promise<MyLessonRowDto[]> => {
  const res = await studentGetClasses();
  if (!res.success) throw new Error(res.error?.message ?? '수업 목록을 불러오지 못했습니다.');
  return (res.data?.classes ?? [])
    .filter((item) => item.enrollmentStatus !== 'COMPLETED')
    .map((item) => ({
      lessonId: item.lessonId ?? 0,
      lessonName: item.lessonName ?? '',
      ended: false,
    }));
};

/** 학부모는 연결 자녀 기준 시험 수업 목록 (`GET /api/lessons/students/my-lessons`) */
const getStudentParentMyLessons = async (): Promise<MyLessonRowDto[]> => {
  const res = await lessonGetMyExamLessons();
  if (!res.success) throw new Error(res.error?.message ?? '수업 목록을 불러오지 못했습니다.');
  return (res.data ?? []).map((item) => ({
    lessonId: item.id ?? 0,
    lessonName: item.lessonName ?? '',
    ended: false,
  }));
};

export const layoutMenuApi = {
  /**
   * 역할별 수업 목록
   * 외부에서는 하나의 함수로 쓰되, 내부적으로는 역할마다 서로 다른 API를 호출한다.
   */
  getMyLessons: async (role: MyLessonsRole): Promise<MyLessonRowDto[]> => {
    switch (role) {
      case USER_ROLE.INSTRUCTOR:
        return getInstructorMyLessons();
      case USER_ROLE.STUDENT:
        return getStudentOngoingLessons();
      case USER_ROLE.PARENT:
        return getStudentParentMyLessons();
      default: {
        // 역할이 추가되면 여기서 컴파일 에러로 잡히고, 타입을 우회한 호출은 런타임에 막힌다
        const unexpectedRole: never = role;
        throw new Error(`Unexpected role for my-lessons: ${unexpectedRole}`);
      }
    }
  },
};
